"use client";
import { createContext, ReactNode, useCallback, useContext, useState } from "react";
import type { NoticeVariant } from "./Notice";
import { NoticeContainer } from "./NoticeContainer";

interface NoticeData {
  id: string;
  message: string;
  variant: NoticeVariant;
  duration: number;
  dismissible: boolean;
}

interface NoticeContextValue {
  notices: NoticeData[];
  addNotice: (message: string, options?: Partial<Omit<NoticeData, "id" | "message">>) => string;
  removeNotice: (id: string) => void;
  clearNotices: () => void;
}

const NoticeContext = createContext<NoticeContextValue | undefined>(undefined);

export interface NoticeProviderProps {
  /** Child components */
  children: ReactNode;
  /** Position of the notice container */
  position?: "top-left" | "top-center" | "top-right" | "bottom-left" | "bottom-center" | "bottom-right";
  /** Maximum number of notices to display at once */
  maxNotices?: number;
  /** Default duration for notices in milliseconds */
  defaultDuration?: number;
  /** Whether notices are dismissible by default */
  defaultDismissible?: boolean;
}

/**
 * NoticeProvider
 *
 * Provides notice functionality to child components.
 * Manages the state of all active notices and renders them in a container.
 */
export function NoticeProvider({
  children,
  position = "bottom-center",
  maxNotices = 5,
  defaultDuration = 5000,
  defaultDismissible = true,
}: NoticeProviderProps) {
  const [notices, setNotices] = useState<NoticeData[]>([]);

  const addNotice = useCallback(
    (message: string, options?: Partial<Omit<NoticeData, "id" | "message">>) => {
      const id = `notice-${Date.now()}-${Math.random()}`;
      const newNotice: NoticeData = {
        id,
        message,
        variant: options?.variant || "info",
        duration: options?.duration ?? defaultDuration,
        dismissible: options?.dismissible ?? defaultDismissible,
      };

      setNotices((prev) => {
        const updated = [...prev, newNotice];
        // Keep only the most recent notices if we exceed maxNotices
        if (updated.length > maxNotices) {
          return updated.slice(-maxNotices);
        }
        return updated;
      });

      return id;
    },
    [maxNotices, defaultDuration, defaultDismissible]
  );

  const removeNotice = useCallback((id: string) => {
    setNotices((prev) => prev.filter((notice) => notice.id !== id));
  }, []);

  const clearNotices = useCallback(() => {
    setNotices([]);
  }, []);

  const value: NoticeContextValue = {
    notices,
    addNotice,
    removeNotice,
    clearNotices,
  };

  return (
    <NoticeContext.Provider value={value}>
      {children}
      <NoticeContainer notices={notices} onDismiss={removeNotice} position={position} />
    </NoticeContext.Provider>
  );
}

/**
 * useNoticeContext
 *
 * Internal hook to access the NoticeContext.
 * Throws an error if used outside of a NoticeProvider.
 */
export function useNoticeContext() {
  const context = useContext(NoticeContext);
  if (!context) {
    throw new Error("useNoticeContext must be used within a NoticeProvider");
  }
  return context;
}
